import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { RecordRow } from "./RecordRow";
import { RecordDetails } from "./RecordDetails";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";

interface HealthRecord {
  id: string;
  type: string;
  title: string;
  content?: string | null;
  text?: string | null;
  risk: string;
  ai_summary?: string | null;
  observed_at: string;
  metadata?: any;
  source_document?: any;
  source_type?: 'smartfile' | 'document' | null;
  source_session_id?: string | null;
  document_id?: string | null;
  source_file_name?: string | null;
  extracted_content?: string | null;
  is_session_container?: boolean;
  items_count?: number;
}

interface PatientHealthRecordsListProps {
  records: HealthRecord[];
  onViewDocument?: (id: string, type: 'smartfile' | 'document') => void;
  itemsCounts?: Record<string, number>;
}

export function PatientHealthRecordsList({ records, onViewDocument, itemsCounts = {} }: PatientHealthRecordsListProps) {
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);
  const [recordsWithFileNames, setRecordsWithFileNames] = useState<HealthRecord[]>(records);

  useEffect(() => {
    fetchFileNames();
  }, [records]);

  async function fetchFileNames() {
    const enrichedRecords = await Promise.all(
      records.map(async (record) => {
        const enriched = { ...record };

        // Buscar nome do arquivo
        if (record.source_session_id) {
          const { data } = await supabase
            .from("smartfile_sessions")
            .select("file_name")
            .eq("id", record.source_session_id)
            .single();
          enriched.source_file_name = data?.file_name;
        } else if (record.document_id) {
          const { data } = await supabase
            .from("documents_onboarding")
            .select("file_name")
            .eq("id", record.document_id)
            .single();
          enriched.source_file_name = data?.file_name;
        }

        // ✅ REMOVIDO: health_record_items não é mais usado
        enriched.items_count = 0;

        return enriched;
      })
    );
    setRecordsWithFileNames(enrichedRecords);
  }

  if (records.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-muted/50 border-b text-xs font-medium text-muted-foreground uppercase">
        <div className="col-span-1"></div>
        <div className="col-span-3 md:col-span-2">Data</div>
        <div className="col-span-4 md:col-span-2">Tipo</div>
        <div className="col-span-4 md:col-span-3">Título</div>
        <div className="col-span-6 md:col-span-2 text-right md:text-left">Risco</div>
        <div className="col-span-6 md:col-span-2 text-right">Origem</div>
      </div>

      {/* Records */}
      <div className="divide-y">
        {recordsWithFileNames.map((record) => (
          <Collapsible
            key={record.id}
            open={openRecordId === record.id}
            onOpenChange={(open) => setOpenRecordId(open ? record.id : null)}
          >
            <CollapsibleTrigger asChild>
              <div className="hover:bg-muted/30 transition-colors border-b last:border-b-0">
                <RecordRow
                  record={record}
                  isOpen={openRecordId === record.id}
                  onToggle={() => setOpenRecordId(openRecordId === record.id ? null : record.id)}
                  itemsCount={record.items_count || itemsCounts[record.id] || 0}
                  onViewDocument={onViewDocument}
                  onDelete={() => {
                    fetchFileNames();
                    window.location.reload();
                  }}
                  sourceFileName={record.source_file_name}
                />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <RecordDetails record={record} onViewDocument={onViewDocument} />
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </Card>
  );
}
